import { observable, computed, action, makeObservable } from 'mobx'
import ReportCollection from '../../State/Collections/ReportCollection'
import SessionStore from '../../State/SessionStore'
import TableStore from '../../Components/TableStore'
import download from 'downloadjs'
import Papa from 'papaparse'
import bind from 'bind-decorator'
import sortPhases from '../../Utils/sortPhases'
import ProjectCollection from '../../State/Collections/ProjectCollection'
import BatchDataStore from '../../State/BatchDataStore'
import { qf } from '../../Queries/queryFormatter'
import { computedFn } from 'mobx-utils'
import { queryClient } from '../../App'

class ProjectListStore {
    @observable queryData = []
    @observable expandAll = false
    @observable exportGroup = 'Projects'
    @observable searchParams = {}
    @observable selectedProjectIds = new Set()
    @observable selectedPhaseIds = new Set()
    @observable batchSaving = false

    constructor() {
        makeObservable(this)
    }

    @action.bound
    init(queryData) {
        this.queryData = queryData
        this.expandAll = false
        this.selectedProjectIds.clear()
        this.selectedPhaseIds.clear()
        this.batchSaving = false
    }

    @action.bound
    reset() {
        this.queryData = []
        this.expandAll = false
        this.exportGroup = 'Projects'
        this.searchParams = {}
        this.selectedProjectIds.clear()
        this.selectedPhaseIds.clear()
        this.batchSaving = false
    }

    @action.bound
    setSearchParams(params) {
        this.searchParams = params
    }

    @computed
    get report() {
        return (
            ReportCollection.reportsById[this.searchParams?.report] ||
            SessionStore.organisation.defaultProjectReport
        )
    }
    @action.bound
    toggleExpandAll() {
        this.expandAll = !this.expandAll
    }
    @action.bound
    setExportGroup(group) {
        this.exportGroup = group
    }
    // --- Selection Logic Start ---
    getLeafRows = computedFn((row) => {
        return [row]
    })

    getAllPhaseIdsForProject(projectRow) {
        return (
            projectRow?.children
                ?.map((phase) => phase.phaseId)
                .filter(Boolean) || []
        )
    }

    getParentProjectForRow(phaseRow) {
        return this.queryData.find((project) =>
            project.children?.some(
                (phase) => phase.phaseId === phaseRow?.phaseId
            )
        )
    }

    isRowSelected = computedFn((row) => {
        if (row?.projectId && !row?.phaseId) {
            return this.selectedProjectIds.has(row.projectId)
        } else if (row?.phaseId) {
            return this.selectedPhaseIds.has(row.phaseId)
        }
        return false
    })

    isProjectPartiallySelected = computedFn((projectRow) => {
        if (!projectRow?.projectId) return false
        const phaseIds = this.getAllPhaseIdsForProject(projectRow)
        const selectedPhaseIdsInSet = phaseIds.filter((id) =>
            this.selectedPhaseIds.has(id)
        )
        return (
            selectedPhaseIdsInSet.length > 0 &&
            selectedPhaseIdsInSet.length < phaseIds.length
        )
    })

    @action.bound
    selectRow(row) {
        if (row?.projectId && !row?.phaseId) {
            this.selectedProjectIds.add(row.projectId)
            this.getAllPhaseIdsForProject(row).forEach((id) =>
                this.selectedPhaseIds.add(id)
            )
        } else if (row?.phaseId) {
            this.selectedPhaseIds.add(row.phaseId)
            const parentProject = this.getParentProjectForRow(row)
            if (parentProject) {
                const allPhaseIds = this.getAllPhaseIdsForProject(parentProject)
                if (allPhaseIds.every((id) => this.selectedPhaseIds.has(id))) {
                    this.selectedProjectIds.add(parentProject.projectId)
                }
            }
        }
    }

    @action.bound
    deselectRow(row) {
        if (row?.projectId && !row?.phaseId) {
            this.selectedProjectIds.delete(row.projectId)
            this.getAllPhaseIdsForProject(row).forEach((id) =>
                this.selectedPhaseIds.delete(id)
            )
        } else if (row?.phaseId) {
            this.selectedPhaseIds.delete(row.phaseId)
            const parentProject = this.getParentProjectForRow(row)
            if (parentProject?.projectId) {
                this.selectedProjectIds.delete(parentProject.projectId)
            }
        }
    }

    @action.bound
    selectAllRows() {
        this.selectedProjectIds.clear()
        this.selectedPhaseIds.clear()
        this.queryData.forEach((projectRow) => {
            if (projectRow?.projectId) {
                this.selectedProjectIds.add(projectRow.projectId)
                this.getAllPhaseIdsForProject(projectRow).forEach((id) =>
                    this.selectedPhaseIds.add(id)
                )
            }
        })
    }

    @action.bound
    deselectAllRows() {
        this.selectedProjectIds.clear()
        this.selectedPhaseIds.clear()
    }

    @action.bound
    batchSaveSelectedRows(updates) {
        this.batchSaving = true

        const projectIdsToUpdate = [...this.selectedProjectIds]
        // const phaseIdsToUpdate = [...this.selectedPhaseIds] // Not directly used for batch update filter, but part of data

        if (!projectIdsToUpdate.length) {
            // Updated check: only proceed if project IDs are selected
            console.warn(
                'Batch save called for projects, but no project IDs were selected.'
            )
            this.batchSaving = false
            return
        }

        BatchDataStore.addData({
            collection: 'projects',
            // filters: projectIdsToFilterBy.length // Old way
            //     ? [[`id in (${qf(projectIdsToFilterBy)})`]]
            //     : [],
            ids: projectIdsToUpdate, // New way
            data: {
                ...updates,
                // The Python `batch_save` took `data` and applied it.
                // The original client code added selectedProjectIds and selectedPhaseIds to this data.
                // If this is still desired, keep them. Otherwise, review if they are needed for the actual update operation.
                selectedProjectIds: projectIdsToUpdate,
                selectedPhaseIds: [...this.selectedPhaseIds],
            },
        })
        BatchDataStore.startSave(() => {
            queryClient.invalidateQueries([this.report.queryKey])
            this.batchSaving = false
            this.selectedProjectIds.clear()
            this.selectedPhaseIds.clear()
        })
    }
    // --- Selection Logic End ---
    @bind
    async downloadCSV() {
        const group = this.exportGroup
        if (group === 'Projects') {
            this.report.downloadCSV()
        } else {
            const tableStore = new TableStore()
            const csvData = this.report.tableStore.rows.flatMap((row, i) => {
                const phases = row.rowObject.children.sort(sortPhases)
                const columns = this.report.tableColumns
                if (!columns.map((c) => c.id).includes('projectName')) {
                    columns.unshift({
                        id: 'projectName',
                        label: 'Project',
                        type: 'text',
                        width: 26,
                        value: (row) => {
                            return ProjectCollection.projectsById[row.projectId]
                                .title
                        },
                    })
                }
                tableStore.update({
                    rows: phases,
                    columns,
                })
                return tableStore.getCsvData({
                    includeHeadings: i === 0,
                })
            })
            download(
                Papa.unparse(csvData),
                `${this.report.name}.csv`,
                'text/csv'
            )
        }
    }
}

export default new ProjectListStore()
